import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { DataService, SharedModule } from "@bb-vendure/admin-ui/core";
import { NgSelectComponent } from "@ng-select/ng-select";
import { concat, merge, Observable, of, Subject, Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  mapTo,
  switchMap,
  tap,
} from "rxjs/operators";

import {
  GetAllCountriesDocument,
  GetAllCountriesQuery,
  SortOrder,
} from "src/generated-gql/graphql";

export type CountryFragment =
  GetAllCountriesQuery["countries"]["items"][number];

@Component({
  standalone: true,
  imports: [SharedModule],
  selector: "multiple-country-selector",
  templateUrl: "./multiple-country-selector.component.html",
  styleUrls: ["./multiple-country-selector.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultipleCountrySelectorComponent),
      multi: true,
    },
  ],
})
export class MultipleCountrySelectorComponent
  implements OnInit, OnDestroy, ControlValueAccessor, OnChanges
{
  @Output() selectedValuesChange = new EventEmitter<CountryFragment[]>();
  @Input() readonly = false;
  @Input() transformControlValueAccessorValue: (
    value: CountryFragment[]
  ) => any[] = (value) => value;
  searchInput$ = new Subject<string>();
  searchLoading = false;
  searchResults$: Observable<CountryFragment[]>;
  selectedCodes$ = new Subject<string[]>();

  @ViewChild(NgSelectComponent) private ngSelect: NgSelectComponent;

  onChangeFn: (val: any) => void;
  onTouchFn: () => void;
  disabled = false;

  @Input()
  defaultValues: CountryFragment[] = [];

  value: Array<string | CountryFragment>;
  private subscription: Subscription;
  constructor(
    private dataService: DataService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["defaultValues"] && changes["defaultValues"].currentValue) {
      this.dataService
        .query(GetAllCountriesDocument, {
          options: {
            take: 100,
            filter: {
              code: {
                in: changes["defaultValues"].currentValue.map((fv) => fv.code),
              },
            },
          },
        })
        .mapSingle((result) => result.countries.items)
        .subscribe((val) => {
          this.value = val;
          this.changeDetectorRef.markForCheck();
        });
    }
  }

  ngOnInit(): void {
    this.initSearchResults();
  }

  private initSearchResults() {
    const searchItems$ = this.searchInput$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => (this.searchLoading = true)),
      switchMap((term) => {
        if (!term) {
          return this.dataService
            .query(GetAllCountriesDocument, {
              options: {
                take: 20,
                sort: {
                  name: SortOrder.Asc,
                },
              },
            })
            .mapSingle((result) => result.countries.items);
        }
        // return this.dataService.facet
        //   .getFacetValues({ take: 100, filter: { name: { contains: term } } })
        //   .mapSingle((result) => result.facetValues.items);

        return this.dataService
          .query(GetAllCountriesDocument, {
            options: {
              take: 100,
              filter: {
                name: {
                  contains: term,
                },
              },
            },
          })
          .mapSingle((result) => result.countries.items);
      }),
      tap(() => (this.searchLoading = false))
    );
    this.subscription = this.selectedCodes$
      .pipe(
        switchMap((codes) => {
          if (!codes.length) {
            return of([]);
          }

          return this.dataService
            .query(GetAllCountriesDocument, {
              options: {
                take: 100,
                filter: {
                  code: {
                    in: codes,
                  },
                },
              },
            })
            .mapSingle((result) => result.countries.items);
        })
      )
      .subscribe((val) => {
        this.value = val;
        this.changeDetectorRef.markForCheck();
      });

    const clear$ = this.selectedValuesChange.pipe(mapTo([]));
    this.searchResults$ = concat(
      this.dataService
        .query(GetAllCountriesDocument, {
          options: {
            take: 20,
            sort: {
              name: SortOrder.Asc,
            },
          },
        })
        .mapSingle((result) => result.countries.items),
      merge(searchItems$, clear$)
    );
  }
  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  onChange(selected: CountryFragment[]) {
    if (this.readonly) {
      return;
    }
    // for (const sel of selected) {
    //   console.log(`selected: ${sel.facet.name}:${sel.code}`);
    // }
    this.selectedValuesChange.emit(selected);
    if (this.onChangeFn) {
      const transformedValue =
        this.transformControlValueAccessorValue(selected);
      this.onChangeFn(transformedValue);
    }
  }

  registerOnChange(fn: any) {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  focus() {
    this.ngSelect.focus();
  }

  writeValue(obj: any): void {
    let valueIds: string[] | undefined;
    if (typeof obj === "string") {
      try {
        const facetValueIds = JSON.parse(obj) as string[];
        valueIds = facetValueIds;
      } catch (err) {
        // TODO: log error
        throw err;
      }
    } else if (Array.isArray(obj)) {
      const isIdArray = (input: unknown[]): input is Array<string | number> =>
        input.every((i) => typeof i === "number" || typeof i === "string");
      if (isIdArray(obj)) {
        valueIds = obj.map((fv) => fv.toString());
      } else {
        valueIds = obj.map((fv) => fv.code);
      }
    } else {
      const isIdArray = (input: unknown[]): input is Array<string | number> =>
        input.every((i) => typeof i === "number" || typeof i === "string");
      if (isIdArray(obj.codes)) {
        valueIds = obj.codes.map((fv) => fv.toString());
      } else {
        valueIds = obj.codes.map((fv) => fv.code);
      }
    }
    if (valueIds) {
      // this.value = valueIds;
      this.selectedCodes$.next(valueIds);
    }
  }
}
